import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, errorMessage, API_BASE } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ENTITY_TYPES, UNITS, VAT_RATES } from "@/lib/salesConstants";
import { BrregNameSearch, BrregOrgField } from "@/components/BrregLookup";
import { entityToFields } from "@/lib/brreg";
import { toast } from "sonner";
import { BadgeCheck, ImagePlus, Loader2, Trash2, AlertTriangle, Building2, Receipt, Eye, Smartphone } from "lucide-react";

const ACCENTS = ["#0E7490", "#1B2430", "#1E7A3A", "#3730A3", "#8A5A00", "#B42318", "#0F766E", "#5B6776"];

export default function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Dyplenker: /profil#purring, /profil#faktura → åpne «Fakturaoppsett»-fanen og rull til seksjonen
  const hashTab = ["#purring", "#faktura", "#nummerering"].includes(location.hash) ? "faktura" : "identitet";
  const [tab, setTab] = useState(hashTab);
  const [p, setP] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoSrc, setLogoSrc] = useState(null);
  const [brregApplied, setBrregApplied] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/profile");
      setP(r.data);
      setForm({ ...r.data, invoice_settings: { ...r.data.invoice_settings } });
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (["#purring", "#faktura", "#nummerering"].includes(location.hash)) setTab("faktura");
  }, [location.hash]);

  useEffect(() => {
    if (!form || tab !== "faktura" || !location.hash) return;
    const id = location.hash.slice(1);
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    return () => clearTimeout(t);
  }, [form, tab, location.hash]);

  // Logo hentes med auth-header og vises som blob (aldri token i URL)
  useEffect(() => {
    let url;
    if (p?.logo) {
      api.get("/profile/logo", { responseType: "blob", params: { v: p.logo.file_id } }).then((r) => { url = URL.createObjectURL(r.data); setLogoSrc(url); }).catch(() => setLogoSrc(null));
    } else setLogoSrc(null);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [p?.logo?.file_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  /** Fyller inn firmaopplysninger fra BRREG (navn, org.nr, adresse, nettside, MVA/Foretaksregisteret). Bank og kontakt beholdes. */
  const applyBrreg = (entity) => {
    const b = entityToFields(entity);
    setBrregApplied(b.org_number);
    setForm((f) => ({
      ...f,
      name: b.name || f.name,
      org_number: b.org_number || f.org_number,
      entity_type: b.entity_type && ENTITY_TYPES[b.entity_type] && b.entity_type !== "annet" ? b.entity_type : f.entity_type,
      address: b.address || f.address, postal_code: b.postal_code || f.postal_code, city: b.city || f.city, country: b.country || f.country || "Norge",
      website: b.website || f.website,
      mva_registered: entity?.vat_registered ?? f.mva_registered,
      foretaksregisteret: entity?.foretaksregisteret ?? f.foretaksregisteret,
    }));
    toast.success("Opplysninger hentet fra Brønnøysundregistrene", { description: "Kontroller og lagre profilen." });
    if (entity?.bankrupt || entity?.deleted_at) toast.warning(entity.deleted_at ? "Virksomheten er slettet i Enhetsregisteret" : "Virksomheten er registrert som konkurs");
  };
  const setS = (k) => (e) => setForm((f) => ({ ...f, invoice_settings: { ...f.invoice_settings, [k]: e?.target ? e.target.value : e } }));

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setError("");
    const s = form.invoice_settings;
    const payload = {
      entity_type: form.entity_type, name: form.name, org_number: form.org_number || "", mva_registered: !!form.mva_registered, foretaksregisteret: !!form.foretaksregisteret,
      address: form.address || "", postal_code: form.postal_code || "", city: form.city || "", country: form.country || "Norge", email: form.email || "", phone: form.phone || "", website: form.website || "",
      bank_account: form.bank_account || "", iban: form.iban || "", bic: form.bic || "",
      invoice_settings: {
        number_prefix: s.number_prefix || "", next_number: Number(s.next_number) || 1, default_due_days: Number(s.default_due_days) || 0, default_vat_rate: Number(s.default_vat_rate), default_unit: s.default_unit || "stk",
        default_note: s.default_note || "", payment_terms: s.payment_terms || "", kid_enabled: !!s.kid_enabled, reminder_fee: Number(s.reminder_fee) || 0, reminder_days_after_due: Number(s.reminder_days_after_due) || 0,
        reminder_suggest_after_days: Number(s.reminder_suggest_after_days) || 14, reminder_max_level: Number(s.reminder_max_level) || 3, reminder_auto_suggest: s.reminder_auto_suggest !== false,
        late_interest_text: s.late_interest_text || "", accent_color: s.accent_color || "#0E7490",
        late_interest_rate: s.late_interest_rate === undefined || s.late_interest_rate === null ? 12 : Number(s.late_interest_rate),
        reminder_inkasso_notice: s.reminder_inkasso_notice !== false, reminder_include_interest: s.reminder_include_interest !== false,
      },
    };
    try {
      const r = await api.put("/profile", payload);
      setP(r.data);
      setForm({ ...r.data, invoice_settings: { ...r.data.invoice_settings } });
      toast.success("Profilen er lagret");
    } catch (err) {
      setError(errorMessage(err));
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP(r.data);
      toast.success("Logo lastet opp");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      const r = await api.delete("/profile/logo");
      setP(r.data);
      toast.success("Logo fjernet");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLogoBusy(false);
    }
  };

  if (error && !form) return <Alert variant="destructive"><AlertTitle>Kunne ikke laste profilen</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>;
  if (!form) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64" /><Skeleton className="h-64" /></div>;

  const isCompany = form.entity_type !== "privat";
  const comp = p?.completeness;

  return (
    <form onSubmit={save} className="space-y-6">
      <PageHeader
        title={isCompany ? "Firmaprofil" : "Min profil"}
        description="Ett felles register for deg eller firmaet ditt. Opplysningene brukes både når du mottar regninger og som avsender på fakturaene du sender."
        actions={<Button type="submit" disabled={saving} data-testid="profile-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BadgeCheck className="h-4 w-4 mr-2" />} Lagre</Button>}
      />

      {comp && !comp.complete ? (
        <Alert className="border-[#FFE2A3] dark:border-[#8A5A00]/50" data-testid="profile-incomplete-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Fullfør profilen for å kunne utstede fakturaer</AlertTitle>
          <AlertDescription>Mangler: {comp.missing.join(", ")}.</AlertDescription>
        </Alert>
      ) : null}

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="identitet" className="gap-2" data-testid="profile-tab-identity"><Building2 className="h-4 w-4" /> Identitet og logo</TabsTrigger>
          <TabsTrigger value="faktura" className="gap-2" data-testid="profile-tab-invoice"><Receipt className="h-4 w-4" /> Fakturaoppsett</TabsTrigger>
        </TabsList>

        <TabsContent value="identitet" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <Card>
              <CardHeader><CardTitle className="text-base">Hvem er du?</CardTitle><CardDescription>Navn og organisasjonsnummer vises på alle fakturaer du sender.</CardDescription></CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-1.5">
                  <Label>Virksomhetstype</Label>
                  <Select value={form.entity_type} onValueChange={set("entity_type")}>
                    <SelectTrigger data-testid="profile-entity-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card">{Object.entries(ENTITY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {isCompany ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="pf-org">Organisasjonsnummer *</Label>
                    <BrregOrgField id="pf-org" value={form.org_number || ""} onChange={(v) => setForm((f) => ({ ...f, org_number: v }))} onApply={applyBrreg} autoApply={!(form.name || "").trim()} appliedOrgNumber={brregApplied} testIdPrefix="profile" />
                  </div>
                ) : null}
                <div className="space-y-1.5">
                  <Label htmlFor="pf-name">{isCompany ? "Firmanavn" : "Fullt navn"} *</Label>
                  {isCompany ? (
                    <BrregNameSearch id="pf-name" value={form.name || ""} onChange={(v) => setForm((f) => ({ ...f, name: v }))} onSelect={applyBrreg} placeholder="Søk etter firmaet i Enhetsregisteret, eller skriv navnet" testIdPrefix="profile" />
                  ) : (
                    <Input id="pf-name" value={form.name || ""} onChange={set("name")} data-testid="profile-name-input" />
                  )}
                </div>
                {isCompany ? (
                  <div className="grid sm:grid-cols-2 gap-3">
                      <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"><span className="text-sm">MVA-registrert</span><Switch checked={!!form.mva_registered} onCheckedChange={set("mva_registered")} data-testid="profile-mva-switch" /></label>
                      {["as", "asa"].includes(form.entity_type) ? <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"><span className="text-sm">Registrert i Foretaksregisteret</span><Switch checked={!!form.foretaksregisteret} onCheckedChange={set("foretaksregisteret")} data-testid="profile-foretak-switch" /></label> : null}
                  </div>
                ) : null}
                <div className="space-y-1.5"><Label htmlFor="pf-address">Adresse *</Label><Input id="pf-address" value={form.address || ""} onChange={set("address")} data-testid="profile-address-input" /></div>
                <div className="grid grid-cols-[120px_1fr_1fr] gap-4">
                  <div className="space-y-1.5"><Label htmlFor="pf-zip">Postnr *</Label><Input id="pf-zip" inputMode="numeric" value={form.postal_code || ""} onChange={set("postal_code")} data-testid="profile-zip-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="pf-city">Sted *</Label><Input id="pf-city" value={form.city || ""} onChange={set("city")} data-testid="profile-city-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="pf-country">Land</Label><Input id="pf-country" value={form.country || ""} onChange={set("country")} data-testid="profile-country-input" /></div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5"><Label htmlFor="pf-email">E-post</Label><Input id="pf-email" type="email" value={form.email || ""} onChange={set("email")} data-testid="profile-email-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="pf-phone">Telefon</Label><Input id="pf-phone" value={form.phone || ""} onChange={set("phone")} data-testid="profile-phone-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="pf-web">Nettside</Label><Input id="pf-web" value={form.website || ""} onChange={set("website")} data-testid="profile-website-input" /></div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Logo</CardTitle><CardDescription>Din egen logo brukes på faktura, kreditnota og purring. PNG, JPG, WebP eller SVG, maks 2 MB.</CardDescription></CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-xl border bg-white dark:bg-white/95 p-4 min-h-[112px] flex items-center justify-center" data-testid="profile-logo-preview">
                    {logoSrc ? <img src={logoSrc} alt="Logo" className="max-h-20 max-w-full object-contain" /> : <p className="text-sm text-muted-foreground text-center">Ingen logo ennå – firmanavnet vises i stedet.</p>}
                  </div>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="profile-logo-input" />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1" disabled={logoBusy} onClick={() => fileRef.current?.click()} data-testid="profile-logo-upload-button">{logoBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ImagePlus className="h-4 w-4 mr-2" />} {p?.logo ? "Bytt logo" : "Last opp logo"}</Button>
                    {p?.logo ? <Button type="button" variant="ghost" size="icon" disabled={logoBusy} onClick={removeLogo} aria-label="Fjern logo" data-testid="profile-logo-remove-button"><Trash2 className="h-4 w-4" /></Button> : null}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Innbetaling</CardTitle><CardDescription>Kontonummeret vises i betalingsboksen på fakturaen.</CardDescription></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5"><Label htmlFor="pf-acc">Kontonummer *</Label><Input id="pf-acc" inputMode="numeric" placeholder="11 siffer" value={form.bank_account || ""} onChange={set("bank_account")} data-testid="profile-account-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label htmlFor="pf-iban">IBAN</Label><Input id="pf-iban" value={form.iban || ""} onChange={set("iban")} data-testid="profile-iban-input" /></div>
                    <div className="space-y-1.5"><Label htmlFor="pf-bic">BIC/SWIFT</Label><Input id="pf-bic" value={form.bic || ""} onChange={set("bic")} data-testid="profile-bic-input" /></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="faktura" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Nummerering og frister</CardTitle><CardDescription>Fakturanummer tildeles automatisk og fortløpende når fakturaen utstedes (krav i bokføringsforskriften).</CardDescription></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-[1fr_1fr] gap-4">
                  <div className="space-y-1.5"><Label htmlFor="is-prefix">Prefiks (valgfritt)</Label><Input id="is-prefix" maxLength={10} placeholder="f.eks. F-" value={form.invoice_settings.number_prefix || ""} onChange={setS("number_prefix")} data-testid="settings-prefix-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="is-next">Neste fakturanummer</Label><Input id="is-next" type="number" min="1" value={form.invoice_settings.next_number ?? ""} onChange={setS("next_number")} data-testid="settings-next-number-input" /></div>
                </div>
                <p className="text-xs text-muted-foreground">Neste faktura får nummer <strong className="text-foreground">{form.invoice_settings.number_prefix || ""}{form.invoice_settings.next_number}</strong>. Nummeret kan bare settes høyere enn siste utstedte.</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5"><Label htmlFor="is-due">Forfall (dager)</Label><Input id="is-due" type="number" min="0" max="365" value={form.invoice_settings.default_due_days ?? ""} onChange={setS("default_due_days")} data-testid="settings-due-days-input" /></div>
                  <div className="space-y-1.5">
                    <Label>Standard MVA</Label>
                    <Select value={String(form.invoice_settings.default_vat_rate ?? 25)} onValueChange={(v) => setS("default_vat_rate")(Number(v))}>
                      <SelectTrigger data-testid="settings-vat-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card">{VAT_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r} %</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Standard enhet</Label>
                    <Select value={form.invoice_settings.default_unit || "stk"} onValueChange={setS("default_unit")}>
                      <SelectTrigger data-testid="settings-unit-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <label className="flex items-start justify-between gap-3 rounded-lg border px-3 py-3">
                  <span><span className="text-sm font-medium block">KID-nummer på fakturaer</span><span className="text-xs text-muted-foreground">Krever OCR/KID-avtale med banken din. Genereres med MOD10-kontrollsiffer.</span></span>
                  <Switch checked={!!form.invoice_settings.kid_enabled} onCheckedChange={setS("kid_enabled")} data-testid="settings-kid-switch" />
                </label>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Tekster og utseende</CardTitle><CardDescription>Vises på fakturadokumentet.</CardDescription></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5"><Label htmlFor="is-terms">Betalingsbetingelser</Label><Textarea id="is-terms" rows={2} value={form.invoice_settings.payment_terms || ""} onChange={setS("payment_terms")} data-testid="settings-terms-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="is-late">Tekst om forsinkelsesrente</Label><Textarea id="is-late" rows={2} value={form.invoice_settings.late_interest_text || ""} onChange={setS("late_interest_text")} data-testid="settings-late-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="is-note">Standard melding på nye fakturaer</Label><Textarea id="is-note" rows={2} placeholder="f.eks. Takk for oppdraget!" value={form.invoice_settings.default_note || ""} onChange={setS("default_note")} data-testid="settings-note-input" /></div>
                <div className="space-y-2">
                  <Label>Aksentfarge på dokumentet</Label>
                  <div className="flex flex-wrap gap-2" data-testid="settings-accent-colors">
                    {ACCENTS.map((c) => (
                      <button key={c} type="button" onClick={() => setS("accent_color")(c)} className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-105 ${form.invoice_settings.accent_color === c ? "border-foreground ring-2 ring-ring ring-offset-2 ring-offset-background" : "border-transparent"}`} style={{ background: c }} aria-label={`Farge ${c}`} data-testid={`settings-accent-${c.replace('#', '')}`} />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card id="purring">
              <CardHeader><CardTitle className="text-base">Purring</CardTitle><CardDescription>Gebyr og frist som foreslås når du sender betalingspåminnelse, og når Controlle skal foreslå purringer automatisk.</CardDescription></CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label htmlFor="is-fee">Purregebyr (kr)</Label><Input id="is-fee" type="number" min="0" step="1" value={form.invoice_settings.reminder_fee ?? ""} onChange={setS("reminder_fee")} data-testid="settings-reminder-fee-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="is-rdays">Ny frist (dager)</Label><Input id="is-rdays" type="number" min="1" max="90" value={form.invoice_settings.reminder_days_after_due ?? ""} onChange={setS("reminder_days_after_due")} data-testid="settings-reminder-days-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="is-sdays">Foreslå purring etter (dager over forfall)</Label><Input id="is-sdays" type="number" min="1" max="120" value={form.invoice_settings.reminder_suggest_after_days ?? ""} onChange={setS("reminder_suggest_after_days")} data-testid="settings-reminder-suggest-days-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="is-maxlvl">Maks antall purringer</Label><Input id="is-maxlvl" type="number" min="1" max="5" value={form.invoice_settings.reminder_max_level ?? ""} onChange={setS("reminder_max_level")} data-testid="settings-reminder-max-level-input" /></div>
                <div className="col-span-2 flex items-center justify-between rounded-lg border bg-secondary px-3 py-2.5">
                  <div><p className="text-sm font-medium">Varsle meg når fakturaer er klare for purring</p><p className="text-xs text-muted-foreground">Daglig varsel i appen med antall fakturaer som kan purres i én omgang.</p></div>
                  <Switch checked={form.invoice_settings.reminder_auto_suggest !== false} onCheckedChange={(v) => setForm((f) => ({ ...f, invoice_settings: { ...f.invoice_settings, reminder_auto_suggest: v } }))} data-testid="settings-reminder-auto-suggest-switch" />
                </div>
                <p className="col-span-2 text-xs text-muted-foreground">Purregebyr kan maksimalt være 1/10 av inkassosatsen, og kan først kreves 14 dager etter forfall. Gebyr legges derfor bare til når fakturaen er minst 14 dager over forfall.</p>
              </CardContent>
            </Card>

            <Card id="inkasso">
              <CardHeader><CardTitle className="text-base">Streng purring: inkassovarsel og renter</CardTitle><CardDescription>Purringene formuleres så strengt som norsk lov tillater – med varsel om inkasso, forsinkelsesrente og videre kostnader.</CardDescription></CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="col-span-2 flex items-center justify-between rounded-lg border bg-secondary px-3 py-2.5">
                  <div><p className="text-sm font-medium">Send purringer som inkassovarsel</p><p className="text-xs text-muted-foreground">Inkassoloven § 9: skriftlig varsel med minst 14 dagers frist om at kravet går til inkasso. Fristen løftes automatisk til 14 dager.</p></div>
                  <Switch checked={form.invoice_settings.reminder_inkasso_notice !== false} onCheckedChange={(v) => setForm((f) => ({ ...f, invoice_settings: { ...f.invoice_settings, reminder_inkasso_notice: v } }))} data-testid="settings-inkasso-notice-switch" />
                </div>
                <div className="col-span-2 flex items-center justify-between rounded-lg border bg-secondary px-3 py-2.5">
                  <div><p className="text-sm font-medium">Legg påløpt forsinkelsesrente til beløpet</p><p className="text-xs text-muted-foreground">Forsinkelsesrenteloven § 3: rente fra forfall til betaling. Beregnes per purring og vises som egen linje.</p></div>
                  <Switch checked={form.invoice_settings.reminder_include_interest !== false} onCheckedChange={(v) => setForm((f) => ({ ...f, invoice_settings: { ...f.invoice_settings, reminder_include_interest: v } }))} data-testid="settings-include-interest-switch" />
                </div>
                <div className="space-y-1.5"><Label htmlFor="is-rate">Forsinkelsesrente (% p.a.)</Label><Input id="is-rate" type="number" min="0" max="30" step="0.25" value={form.invoice_settings.late_interest_rate ?? ""} onChange={setS("late_interest_rate")} data-testid="settings-interest-rate-input" /><p className="text-[11px] text-muted-foreground">Fastsettes av Finansdepartementet 1. januar og 1. juli. Kontroller gjeldende sats på regjeringen.no.</p></div>
                <div className="rounded-lg border p-3 text-xs text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">Purringen inneholder da</p>
                  <p>Krav om betaling innen fristen · inkassovarsel (§ 9) · purregebyr · beregnet forsinkelsesrente · standardkompensasjon for næringsdrivende (§ 3a) · varsel om forliksråd/namsmyndighet · frist for innsigelser.</p>
                </div>
              </CardContent>
            </Card>

            <Card className="flex flex-col">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Smartphone className="h-4 w-4 text-primary" /> Betaling med Vipps og kort</CardTitle><CardDescription>Legg betalingslenke og QR-kode på fakturaer og purringer, og få fakturaen markert som betalt automatisk.</CardDescription></CardHeader>
              <CardContent className="mt-auto">
                <Button type="button" variant="outline" onClick={() => navigate("/faktura/betaling")} data-testid="profile-vipps-link"><Smartphone className="h-4 w-4 mr-2" /> Sett opp Vipps</Button>
              </CardContent>
            </Card>

            <Card className="flex flex-col">
              <CardHeader><CardTitle className="text-base">Slik ser det ut</CardTitle><CardDescription>Se en faktura med dine opplysninger og din logo.</CardDescription></CardHeader>
              <CardContent className="mt-auto">
                <Button type="button" variant="outline" onClick={() => navigate("/faktura/fakturaer/ny")} data-testid="profile-preview-button"><Eye className="h-4 w-4 mr-2" /> Lag en faktura og forhåndsvis</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {error ? <p className="text-sm text-destructive" role="alert" data-testid="profile-error">{error}</p> : null}
      <div className="flex justify-end"><Button type="submit" disabled={saving} data-testid="profile-save-button-bottom">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Lagre profil</Button></div>
    </form>
  );
}
